import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../App';
import PageMetadata from '../components/PageMetadata';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '5rem' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div>
        <PageMetadata
          title="Article Not Found | Adhva Academy"
          description="The requested Adhva Academy article could not be found."
          noIndex
        />
        <Navbar />
        <div className="py-8 container text-center" style={{ paddingTop: '8rem' }}>
          <h1>Article Not Found</h1>
          <Link to="/blog" className="btn-primary" style={{ marginTop: '2rem' }}>Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--background)' }}>
      <PageMetadata
        title={`${article.title} | Adhva Academy`}
        description={article.excerpt || `Read ${article.title}, an exam-preparation insight from Adhva Academy.`}
      />
      <Navbar />
      
      <article style={{ paddingTop: '6rem', paddingBottom: '4rem', minHeight: '100vh' }}>
        {/* Banner Image - Full Width */}
        {article.banner_image && (
          <div style={{ 
            width: '100%', 
            maxWidth: '1400px', 
            margin: '0 auto 3rem auto',
            padding: '0 2rem'
          }}>
            <img 
              src={article.banner_image} 
              alt={article.title} 
              style={{ 
                width: '100%', 
                height: '500px', 
                objectFit: 'cover', 
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
              }} 
              data-testid="banner-image" 
            />
          </div>
        )}

        {/* Article Content Container - Newspaper Style */}
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          padding: '0 3rem',
          background: 'var(--card)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.08)',
          borderRadius: '12px',
          position: 'relative'
        }}>
          {/* Decorative Top Border */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60%',
            height: '4px',
            background: 'linear-gradient(90deg, transparent, var(--primary), transparent)',
            borderRadius: '0 0 2px 2px'
          }}></div>

          {/* Article Header */}
          <div style={{ 
            paddingTop: '3rem',
            paddingBottom: '2rem',
            borderBottom: '2px solid var(--border)',
            marginBottom: '2.5rem'
          }}>
            <h1 style={{ 
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', 
              marginBottom: '1.5rem',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--foreground)',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              textAlign: 'center',
              letterSpacing: '-0.02em'
            }} data-testid="article-title">
              {article.title}
            </h1>
            
            {/* Metadata Bar */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '1rem', 
              flexWrap: 'wrap', 
              color: 'var(--muted-foreground)',
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              <span data-testid="author" style={{ 
                color: 'var(--foreground)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.85rem'
              }}>
                By {article.author}
              </span>
              <span style={{ fontSize: '0.75rem' }}>●</span>
              <span data-testid="date">
                {new Date(article.publish_date || article.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              {article.category && (
                <>
                  <span style={{ fontSize: '0.75rem' }}>●</span>
                  <span style={{ 
                    background: 'var(--primary)', 
                    color: 'white', 
                    padding: '0.35rem 1rem', 
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {article.category}
                  </span>
                </>
              )}
            </div>
          </div>

          {user && user.id === article.author_id && (
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <Link to={`/admin/dashboard?tab=articles&edit=${article.id}`} className="btn-secondary" data-testid="edit-btn">
                ✏️ Edit Article
              </Link>
            </div>
          )}

          {/* Article Body - Newspaper Style */}
          <div 
            style={{ 
              fontSize: '1.125rem', 
              lineHeight: 1.9,
              color: 'var(--foreground)',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              textAlign: 'justify',
              paddingBottom: '3rem',
              columnRule: 'none',
              hyphens: 'auto',
              wordSpacing: '0.05em',
              letterSpacing: '0.01em'
            }}
            className="article-content-newspaper"
            dangerouslySetInnerHTML={{ __html: article.content }}
            data-testid="article-content"
          />

          {/* Tags Section */}
          {article.tags && article.tags.length > 0 && (
            <div style={{ 
              marginTop: '2rem',
              paddingTop: '2rem', 
              paddingBottom: '2rem',
              borderTop: '2px solid var(--border)',
              borderBottom: '2px solid var(--border)'
            }}>
              <h4 style={{ 
                fontSize: '0.875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '1rem',
                color: 'var(--muted-foreground)'
              }}>
                Related Topics
              </h4>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {article.tags.map((tag, idx) => (
                  <span 
                    key={idx} 
                    style={{ 
                      background: 'var(--secondary)', 
                      color: 'var(--primary)', 
                      padding: '0.5rem 1.25rem', 
                      borderRadius: '6px', 
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      border: '1px solid var(--border)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back Button */}
          <div style={{ 
            marginTop: '2rem',
            paddingTop: '2rem',
            paddingBottom: '3rem',
            textAlign: 'center' 
          }}>
            <Link to="/blog" className="btn-secondary" style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 600
            }}>
              ← Back to All Articles
            </Link>
          </div>
        </div>

        {/* Bottom Spacing */}
        <div style={{ height: '4rem' }}></div>
      </article>

      <Footer />

      {/* Custom Styles for Article Content */}
      <style dangerouslySetInnerHTML={{ __html: `
        .article-content-newspaper p {
          margin-bottom: 1.5rem;
          text-indent: 2em;
        }
        .article-content-newspaper p:first-child::first-letter {
          font-size: 4rem;
          font-weight: 700;
          line-height: 0.8;
          float: left;
          margin: 0.1em 0.1em 0 0;
          color: var(--primary);
          font-family: 'Georgia', serif;
        }
        .article-content-newspaper h1,
        .article-content-newspaper h2,
        .article-content-newspaper h3 {
          font-family: 'Georgia', 'Times New Roman', serif;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.3;
          color: var(--foreground);
        }
        .article-content-newspaper h2 {
          font-size: 1.75rem;
          border-bottom: 2px solid var(--border);
          padding-bottom: 0.5rem;
        }
        .article-content-newspaper h3 {
          font-size: 1.4rem;
        }
        .article-content-newspaper blockquote {
          border-left: 4px solid var(--primary);
          padding-left: 1.5rem;
          margin: 2rem 0;
          font-style: italic;
          color: var(--muted-foreground);
          background: var(--secondary);
          padding: 1.5rem;
          border-radius: 8px;
        }
        .article-content-newspaper ul,
        .article-content-newspaper ol {
          margin: 1.5rem 0;
          padding-left: 2.5rem;
        }
        .article-content-newspaper li {
          margin-bottom: 0.75rem;
        }
        .article-content-newspaper a {
          color: var(--primary);
          text-decoration: underline;
          font-weight: 600;
        }
        .article-content-newspaper img {
          max-width: 100%;
          height: auto;
          margin: 2rem 0;
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }
        .article-content-newspaper code {
          background: var(--secondary);
          padding: 0.2em 0.4em;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
        }
        .article-content-newspaper pre {
          background: var(--secondary);
          padding: 1.5rem;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        @media (max-width: 768px) {
          .article-content-newspaper p:first-child::first-letter {
            font-size: 3rem;
          }
        }
      `}} />
    </div>
  );
};

export default BlogDetailPage;
